import React, { useEffect, useState, useMemo } from "react";
import {
  Download,
  Copy,
  FileSpreadsheet,
  FileText,
  FileType,
} from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "../../util/axiosInstance";
import { toast } from "react-toastify";
import { ChevronDown } from "lucide-react";
type Unit = { id: string; name: string };

type ReportItem = {
  year: number;
  month: number;
  down: number;
  up: number;
  customer: number;
  precision: number;
  shutDowns: number;
};

interface MonthlyReportsProps {
  units: Unit[];
  loadingUnitSearchData: boolean;
}
const Row = ({ label, value }: { label: string; value: any }) => (
  <div className="flex justify-between">
    <span>{label}</span>
    <span>{value}</span>
  </div>
);
export default function MonthlyReports({
  units,
  loadingUnitSearchData,
}: MonthlyReportsProps) {
  const getLastSixMonths = () => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const now = new Date();
    const current = now.getMonth();
    const arr: { label: string; value: number; year: number }[] = [];

    for (let i = 1; i <= 6; i++) {
      const d = new Date(now.getFullYear(), current - i, 1);
      arr.push({
        label: months[d.getMonth()],
        value: d.getMonth() + 1,
        year: d.getFullYear(),
      });
    }
    return arr.reverse();
  };

  const lastSixMonths = getLastSixMonths();
  const [selectedMonth, setSelectedMonth] = useState(lastSixMonths[5].value);
  const [selectedYear, setSelectedYear] = useState(lastSixMonths[5].year);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportData, setReportData] = useState<Record<
    string,
    ReportItem
  > | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState("Unit Name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedUnit, setExpandedUnit] = useState<string | null>(null);

  const unitMap =
    units?.reduce((acc: Record<string, string>, u) => {
      acc[u.id] = u.name;
      return acc;
    }, {}) || {};

  const filteredReportData = reportData
    ? Object.entries(reportData).filter(([unitId]) =>
        (unitMap[unitId] || unitId)
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      )
    : [];

  const fetchMonthlyReport = async (month: number, year: number) => {
    if (!units?.length) return;
    try {
      setLoadingReport(true);
      setError(null);

      const payload = { year, month, units: units.map((u) => u.id) };
      //console.log("payload : ", payload);
      const response = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/reports/precision-report-month`,
        payload,
        {
          headers: { id: "anything", "Content-Type": "application/json" },
        }
      );

      setReportData(response.data.data.mydict);
    } catch (err) {
      console.error(err);
      setError("Error fetching report data");
    } finally {
      setLoadingReport(false);
    }
  };

  useEffect(() => {
    if (!loadingUnitSearchData && units?.length) {
      fetchMonthlyReport(selectedMonth, selectedYear);
    }
  }, [selectedMonth, selectedYear, units, loadingUnitSearchData]);

  useEffect(() => setPage(0), [searchQuery]);

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = useMemo(() => {
    if (!filteredReportData) return [];
    return [...filteredReportData].sort(([aId, a], [bId, b]) => {
      let aValue: any;
      let bValue: any;

      switch (orderBy) {
        case "Unit Name":
          aValue = unitMap[aId] || aId;
          bValue = unitMap[bId] || bId;
          break;
        case "Year":
          aValue = a.year;
          bValue = b.year;
          break;
        case "Month":
          aValue = a.month;
          bValue = b.month;
          break;
        case "Stop(Hrs)":
          aValue = a.down;
          bValue = b.down;
          break;
        case "Run(Hrs)":
          aValue = a.up;
          bValue = b.up;
          break;
        case "Customer(Hrs)":
          aValue = a.customer;
          bValue = b.customer;
          break;
        case "PRC(Hrs)":
          aValue = a.precision;
          bValue = b.precision;
          break;
        case "Shutdowns":
          aValue = a.shutDowns;
          bValue = b.shutDowns;
          break;
        default:
          return 0;
      }

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredReportData, order, orderBy]);

  const paginatedUnits = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // --- EXPORT HELPERS ----------------------------------
  const copyToClipboard = () => {
    if (!filteredReportData.length) return;

    const textContent = filteredReportData
      .map(
        ([unitId, r]) =>
          `${unitMap[unitId] || unitId}\t${r.year}\t${r.month}\t${r.down}\t${
            r.up
          }\t${r.customer}\t${r.precision}\t${r.shutDowns}`
      )
      .join("\n");

    const headers =
      "Unit Name\tYear\tMonth\tStop(Hrs)\tRun(Hrs)\tCustomer(Hrs)\tPRC(Hrs)\tShutdowns";

    navigator.clipboard
      .writeText(`${headers}\n${textContent}`)
      .then(() => toast.success("Monthly report copied to clipboard"));
  };

  const exportToCSV = () => {
    if (!filteredReportData.length) return;

    const csvContent = [
      [
        "Unit Name",
        "Year",
        "Month",
        "Stop (Hrs)",
        "Run (Hrs)",
        "Customer (Hrs)",
        "PRC (Hrs)",
        "Shutdowns",
      ],
      ...filteredReportData.map(([unitId, r]) => [
        unitMap[unitId] || unitId,
        r.year,
        r.month,
        r.down,
        r.up,
        r.customer,
        r.precision,
        r.shutDowns,
      ]),
    ];

    const csvString = csvContent
      .map((row) => row.map((v) => `"${v}"`).join(","))
      .join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "Monthly_Report.csv";
    link.click();

    toast.success("Exported Monthly Report to CSV");
  };

  const exportToExcel = () => {
    if (!filteredReportData.length) return;

    const headers = [
      "Unit Name",
      "Year",
      "Month",
      "Stop (Hrs)",
      "Run (Hrs)",
      "Customer (Hrs)",
      "PRC (Hrs)",
      "Shutdowns",
    ];
    const rows = filteredReportData.map(([unitId, r]) => [
      unitMap[unitId] || unitId,
      r.year,
      r.month,
      r.down,
      r.up,
      r.customer,
      r.precision,
      r.shutDowns,
    ]);

    const sheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, "Monthly Report");
    XLSX.writeFile(wb, "Monthly_Report.xlsx");

    toast.success("Exported Monthly Report to Excel");
  };

  const exportToPDF = () => {
    if (!filteredReportData.length) return;

    const doc = new jsPDF("l", "pt", "a4");
    doc.setFontSize(12);
    doc.text("Monthly Units Report", 40, 30);

    const headers = [
      [
        "Unit Name",
        "Year",
        "Month",
        "Stop (Hrs)",
        "Run (Hrs)",
        "Customer (Hrs)",
        "PRC (Hrs)",
        "Shutdowns",
      ],
    ];

    const rows = filteredReportData.map(([unitId, r]) => [
      unitMap[unitId] || unitId,
      r.year,
      new Date(r.year, r.month - 1).toLocaleString("default", {
        month: "long",
      }),
      r.down,
      r.up,
      r.customer,
      r.precision,
      r.shutDowns,
    ]);

    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 50,
      styles: { fontSize: 8, cellPadding: 3 },
    });

    doc.save("Monthly_Report.pdf");
    toast.success("Exported Monthly Report to PDF");
  };

  // -----------------------------------------------------

  return (
    <div className="w-full flex flex-col rounded-lg bg-slate-900 p-5 ">
      {loadingUnitSearchData && (
        <div className="absolute inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-20">
          <div className="animate-spin h-12 w-12 border-4 border-blue-400 border-t-transparent rounded-full" />
        </div>
      )}

      <div className="flex justify-between items-center flex-wrap gap-2 px-4 mt-2 mb-4">
        <h1 className="text-xl font-bold mt-5">Monthly Runtime Report</h1>
      </div>

      {loadingReport ? (
        <div className="w-full flex justify-center items-center min-h-[200px]">
          <div className="animate-spin h-12 w-12 border-4 border-blue-400 border-t-transparent rounded-full" />
        </div>
      ) : error ? (
        <div className="text-red-500 font-bold text-center py-4">{error}</div>
      ) : (
        <>
          <div className="p-3 bg-slate-900 flex flex-col gap-3 md:flex-row md:items-center md:gap-2">
            {/* Search */}
            <input
              type="text"
              placeholder="Search Unit..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="px-2 py-2 rounded bg-slate-800 text-white border border-slate-600 w-full md:flex-1"
            />

            {/* Action Buttons */}
            <div className="grid grid-cols-4 gap-2 sm:flex sm:gap-2 sm:w-auto">
              {" "}
              <button
                onClick={copyToClipboard}
                className="flex items-center justify-center gap-1 px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 text-sm whitespace-nowrap"
              >
                <Copy size={12} className="inline-flex align-middle" /> Copy
              </button>
              <button
                onClick={exportToCSV}
                className="flex items-center justify-center gap-1 px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 text-sm whitespace-nowrap"
              >
                <FileText size={12} className="inline-flex align-middle" /> CSV
              </button>
              <button
                onClick={exportToExcel}
                className="flex items-center justify-center gap-1 px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 text-sm whitespace-nowrap"
              >
                <FileSpreadsheet
                  size={12}
                  className="inline-flex align-middle"
                />{" "}
                Excel
              </button>
              <button
                onClick={exportToPDF}
                className="flex items-center justify-center gap-1 px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 text-sm whitespace-nowrap"
              >
                <FileType size={12} className="inline-flex align-middle" /> PDF
              </button>
            </div>

            {/* Month Select */}
            <select
              value={selectedMonth}
              onChange={(e) => {
                const chosen = lastSixMonths.find(
                  (m) => m.value === Number(e.target.value)
                );
                if (chosen) {
                  setSelectedMonth(chosen.value);
                  setSelectedYear(chosen.year);
                }
              }}
              className="px-2 py-2 bg-gray-800 text-white rounded border border-gray-600 w-full md:w-auto"
            >
              {lastSixMonths.map((m) => (
                <option key={m.label} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          <div className="overflow-auto max-h-[63vh] px-2 mt-5">
            <div className="rounded-xl overflow-hidden border border-gray-700">
              <table className="w-full text-left  border-collapse">
                <thead className="hidden sm:table-header-group bg-gray-700 text-white sticky top-0">
                  <tr className="border-b border-slate-700 bg-slate-800">
                    {[
                      "Unit Name",
                      "Year",
                      "Month",
                      "Stop(Hrs)",
                      "Run(Hrs)",
                      "Customer(Hrs)",
                      "PRC(Hrs)",
                      "Shutdowns",
                    ].map((col) => (
                      <th
                        key={col}
                        className="p-2 cursor-pointer select-none font-semibold text-center"
                        onClick={() => handleSort(col)}
                      >
                        <div className="flex items-center justify-center w-full">
                          <span className="flex items-center gap-1">{col}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedUnits.length ? (
                    paginatedUnits.map(([unitId, r], idx) => (
                      <React.Fragment key={unitId}>
                        {/* Desktop row */}
                        <tr
                          className={`hidden sm:table-row ${
                            idx % 2 === 0 ? "bg-gray-800" : "bg-gray-700"
                          }`}
                        >
                          <td className="p-2">{unitMap[unitId] || unitId}</td>
                          <td className="p-2 text-center">{r.year}</td>
                          <td className="p-2 text-center">
                            {new Date(r.year, r.month - 1).toLocaleString(
                              "default",
                              {
                                month: "long",
                              }
                            )}
                          </td>
                          <td className="p-2 text-center">
                            {r.down.toFixed(1)}
                          </td>
                          <td className="p-2 text-center">{r.up.toFixed(1)}</td>
                          <td className="p-2 text-center">
                            {r.customer.toFixed(1)}
                          </td>
                          <td className="p-2 text-center">
                            {r.precision.toFixed(1)}
                          </td>
                          <td className="p-2 text-center">{r.shutDowns}</td>
                        </tr>

                        {/* Mobile accordion row */}
                        <tr className="sm:hidden bg-gray-800">
                          <td colSpan={8} className="p-2">
                            <button
                              onClick={() =>
                                setExpandedUnit(
                                  expandedUnit === unitId ? null : unitId
                                )
                              }
                              className="w-full flex justify-between items-center text-left"
                            >
                              <span className="font-semibold text-[14px] text-white">
                                {unitMap[unitId] || unitId}
                              </span>
                              <ChevronDown
                                className={`w-4 h-4 transition-transform ${
                                  expandedUnit === unitId ? "rotate-180" : ""
                                }`}
                              />
                            </button>

                            {expandedUnit === unitId && (
                              <div className="mt-3 space-y-2 text-sm text-gray-300">
                                <Row label="Year" value={r.year} />
                                <Row
                                  label="Month"
                                  value={new Date(
                                    r.year,
                                    r.month - 1
                                  ).toLocaleString("default", {
                                    month: "long",
                                  })}
                                />
                                <Row
                                  label="Stop (Hrs)"
                                  value={r.down.toFixed(1)}
                                />
                                <Row
                                  label="Run (Hrs)"
                                  value={r.up.toFixed(1)}
                                />
                                <Row
                                  label="Customer (Hrs)"
                                  value={r.customer.toFixed(1)}
                                />
                                <Row
                                  label="PRC (Hrs)"
                                  value={r.precision.toFixed(1)}
                                />
                                <Row label="Shutdowns" value={r.shutDowns} />
                              </div>
                            )}
                          </td>
                        </tr>
                      </React.Fragment>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={8}
                        className="text-center py-4 text-gray-300"
                      >
                        No units found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex justify-end items-center py-2 px-3 text-white gap-2">
            <div className="w-32 text-right">Rows:</div>
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(0);
              }}
              className="bg-gray-800 border border-gray-600 rounded px-1 py-1"
            >
              {[5, 10, 25, 50, 100].map((n) => (
                <option key={n}>{n}</option>
              ))}
            </select>
          </div>
        </>
      )}

      {/* Export Buttons Row */}
    </div>
  );
}
